// Labor/wage + markup constants (§5.12 / §5.16). Editable defaults; admin manages
// in Phase 8. Wage RATES are seeded in the DB (migration 0003, CA/NV/Davis-Bacon,
// ID scaffolded) so they can be edited without a deploy. These constants are the
// deterministic glue: category→classification, shift multipliers, default markups.

import type { LaborCategory } from './assemblies';

// BOM labor category → wage classification name (matches seeded labor_rate rows).
export const CATEGORY_CLASSIFICATION: Record<LaborCategory, string> = {
  electrician: 'Electrician (Inside Wireman)',
  sound_comm: 'Sound & Communication Installer',
  technician: 'Low-Voltage Technician',
};

// Off-shift multipliers applied to the base wage (§5.12). Editable in admin.
export type Shift = 'straight' | 'ot' | 'double' | 'swing' | 'graveyard' | 'weekend' | 'holiday';
export const SHIFT_MULTIPLIERS: Record<Shift, number> = {
  straight: 1.0, ot: 1.5, double: 2.0, swing: 1.125, graveyard: 1.15, weekend: 1.5, holiday: 2.0,
};
export const SHIFT_LABELS: Record<Shift, string> = {
  straight: 'Straight time', ot: 'Overtime (1.5×)', double: 'Double time (2×)',
  swing: '2nd / swing shift', graveyard: 'Night / graveyard', weekend: 'Weekend', holiday: 'Holiday',
};

// Pricing hierarchy (§5.11) — lower tier number wins.
export const PRICE_TIER: Record<string, number> = {
  supplier_quote: 1, distributor: 2, manual: 3, internet: 4, historical: 5,
};
export const PRICE_TIER_LABEL: Record<string, string> = {
  supplier_quote: 'Approved supplier quote', distributor: 'Distributor price list',
  manual: 'Admin pricing DB', internet: 'Internet-estimated', historical: 'Historical estimate',
};

// Default project markups (§5.16). Seeded per project on first access; editable.
export interface MarkupDef { name: string; label: string; pct: number; basis: string }
export const DEFAULT_MARKUPS: MarkupDef[] = [
  { name: 'material_markup', label: 'Material markup', pct: 15, basis: 'pct of material cost' },
  { name: 'overhead', label: 'Overhead', pct: 10, basis: 'pct of direct cost' },
  { name: 'profit', label: 'Profit', pct: 10, basis: 'pct of (direct cost + overhead)' },
  { name: 'sales_tax', label: 'Sales tax', pct: 8.0, basis: 'pct of material cost' },
  { name: 'freight', label: 'Freight', pct: 2, basis: 'pct of material cost' },
  { name: 'small_tools', label: 'Small tools & consumables', pct: 1.5, basis: 'pct of labor cost' },
  { name: 'bond', label: 'Bond', pct: 0, basis: 'pct of sell price' },
  { name: 'contingency', label: 'Contingency', pct: 3, basis: 'pct of direct cost' },
];

// Selectable travel & misc cost types (§5.13).
export const COST_ADDER_TYPES: { type: string; label: string; unit: string }[] = [
  { type: 'per_diem', label: 'Per diem', unit: 'day' },
  { type: 'hotel', label: 'Hotel', unit: 'night' },
  { type: 'airfare', label: 'Airfare', unit: 'trip' },
  { type: 'rental', label: 'Rental car', unit: 'day' },
  { type: 'parking', label: 'Parking', unit: 'day' },
  { type: 'mileage', label: 'Mileage', unit: 'mile' },
  { type: 'fuel', label: 'Fuel', unit: 'fill' },
  { type: 'travel_time', label: 'Travel time (labor hrs)', unit: 'hour' },
  { type: 'mobilization', label: 'Mobilization', unit: 'ls' },
  { type: 'demobilization', label: 'Demobilization', unit: 'ls' },
  { type: 'lift_rental', label: 'Lift rental (scissor/boom)', unit: 'week' },
  { type: 'permit', label: 'Permit', unit: 'ls' },
  { type: 'engineering', label: 'Engineering / design', unit: 'hour' },
  { type: 'programming', label: 'Programming', unit: 'hour' },
  { type: 'testing', label: 'Testing / commissioning', unit: 'hour' },
  { type: 'training', label: 'Training', unit: 'hour' },
  { type: 'closeout', label: 'Closeout / O&M docs', unit: 'ls' },
];
